/**
 * OAuthButtons — Google + Apple Sign-In buttons for parents.
 *
 * Behavior:
 *   1. On mount, hits GET /api/auth/oauth-config to learn which providers
 *      are enabled AND to get the public Google Client ID (web).
 *   2. If both providers are disabled, renders nothing (parent screens
 *      keep only email/password).
 *   3. If a provider is enabled but the SDK hasn't loaded yet, renders a
 *      disabled placeholder button labelled "Loading…".
 *   4. Google button uses Google Identity Services (GSI) — dynamically
 *      injects https://accounts.google.com/gsi/client. On credential
 *      callback we POST /api/auth/parent/oauth/google with the id_token
 *      and hand back the auth response to the parent via onSuccess.
 *   5. Apple button uses Apple JS SDK — dynamically injects
 *      https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js.
 *      On success we POST /api/auth/parent/oauth/apple with identityToken
 *      + optional name.
 *
 * The parent screen owns the routing decision after auth succeeds. This
 * component only knows how to obtain a provider token and exchange it.
 *
 * IMPORTANT: no client secrets are shipped — the Google Client ID is
 * public per Google's design; Apple Services ID is public too.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";

const GSI_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
const APPLE_SCRIPT_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

// Single global loader map so multiple mounted instances share the same
// promise for each script tag.
const _scriptPromises = new Map();
function loadScript(src) {
  if (_scriptPromises.has(src)) return _scriptPromises.get(src);
  const p = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`failed to load ${src}`));
    document.head.appendChild(s);
  });
  _scriptPromises.set(src, p);
  return p;
}

export function OAuthButtons({
  legalVersions,        // { privacy, terms } for new-user consent
  acceptedLegal = true, // whether the user has ticked the "I agree" box (only matters for register)
  onSuccess,            // ({ isNew, requiresLegalAcceptance }) => void
  onError,              // (message: string) => void
  mode = "signin",      // "signin" | "signup" — affects button label only
}) {
  const [cfg, setCfg] = useState(null);
  const [cfgLoaded, setCfgLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const [appleReady, setAppleReady] = useState(false);
  const googleBtnRef = useRef(null);

  const call = useCallback(async (endpoint, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, {
        ...body,
        acceptPrivacyPolicyVersion: legalVersions?.privacy || "",
        acceptTermsOfServiceVersion: legalVersions?.terms || "",
      });
      onSuccess && onSuccess({
        isNew: !!data.isNew,
        requiresLegalAcceptance: !!data.requiresLegalAcceptance,
      });
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      onError && onError(msg);
    } finally {
      setBusy(false);
    }
  }, [legalVersions, onSuccess, onError]);

  // Fetch config once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/oauth-config");
        if (!cancelled) setCfg(data);
      } catch (_e) {
        if (!cancelled) setCfg({ googleEnabled: false, appleEnabled: false });
      } finally {
        if (!cancelled) setCfgLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load Google GSI + render button.
  useEffect(() => {
    if (!cfg?.googleEnabled || !cfg.googleClientIdWeb) return;
    if (mode === "signup" && !acceptedLegal) return;  // don't render active button before consent
    let cancelled = false;
    loadScript(GSI_SCRIPT_SRC).then(() => {
      if (cancelled || !window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: cfg.googleClientIdWeb,
        callback: (resp) => {
          if (!resp?.credential) {
            onError && onError("Google sign-in returned no credential.");
            return;
          }
          call("/auth/parent/oauth/google", { idToken: resp.credential });
        },
        ux_mode: "popup",
        auto_select: false,
      });
      if (googleBtnRef.current) {
        // Clear + re-render in case config changed.
        googleBtnRef.current.innerHTML = "";
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: "outline",
          size: "large",
          shape: "pill",
          text: mode === "signup" ? "signup_with" : "continue_with",
          logo_alignment: "center",
          width: 320,
        });
      }
      setGoogleReady(true);
    }).catch((e) => onError && onError(`Google SDK failed: ${e.message}`));
    return () => { cancelled = true; };
  }, [cfg, mode, acceptedLegal, call, onError]);

  // Load Apple JS + init.
  useEffect(() => {
    if (!cfg?.appleEnabled || !cfg.appleServicesId) return;
    let cancelled = false;
    loadScript(APPLE_SCRIPT_SRC).then(() => {
      if (cancelled || !window.AppleID?.auth) return;
      window.AppleID.auth.init({
        clientId: cfg.appleServicesId,
        scope: "email name",
        redirectURI: window.location.origin + "/parent/oauth/apple/callback",
        state: "wmleye_signin",
        usePopup: true,
      });
      setAppleReady(true);
    }).catch((e) => onError && onError(`Apple SDK failed: ${e.message}`));
    return () => { cancelled = true; };
  }, [cfg, onError]);

  const handleApple = useCallback(async () => {
    if (!window.AppleID?.auth) return;
    if (mode === "signup" && !acceptedLegal) {
      onError && onError("Please accept the Privacy Policy and Terms of Service first.");
      return;
    }
    try {
      const res = await window.AppleID.auth.signIn();
      const idToken = res?.authorization?.id_token;
      if (!idToken) {
        onError && onError("Apple sign-in returned no identity token.");
        return;
      }
      const name = res?.user?.name
        ? { firstName: res.user.name.firstName, lastName: res.user.name.lastName }
        : null;
      await call("/auth/parent/oauth/apple", {
        identityToken: idToken,
        name,
      });
    } catch (e) {
      // Apple returns { error: "popup_closed_by_user" } etc. Ignore user-cancels silently.
      if (e?.error !== "popup_closed_by_user") {
        onError && onError(`Apple sign-in failed: ${e?.error || e?.message || "unknown"}`);
      }
    }
  }, [mode, acceptedLegal, call, onError]);

  if (!cfgLoaded) return null;  // avoid flicker before we know
  const anyEnabled = cfg?.googleEnabled || cfg?.appleEnabled;
  if (!anyEnabled) return null;

  const disabledForConsent = mode === "signup" && !acceptedLegal;

  return (
    <div className="oauth-buttons" data-testid="oauth-buttons"
         style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 18 }}>
      {cfg.googleEnabled ? (
        <div style={{ position: "relative" }}>
          {/* GSI renders inside this div. We reserve a min-height so layout doesn't jump. */}
          <div ref={googleBtnRef}
               data-testid="oauth-google-button"
               style={{ minHeight: 44,
                        opacity: disabledForConsent ? 0.4 : 1,
                        pointerEvents: disabledForConsent ? "none" : "auto" }} />
          {!googleReady && (
            <div style={{
              position: "absolute", inset: 0, display: "flex",
              alignItems: "center", justifyContent: "center",
              color: "#8a827a", fontSize: 13, pointerEvents: "none",
            }}>Loading Google…</div>
          )}
        </div>
      ) : null}
      {cfg.appleEnabled ? (
        <button
          type="button"
          data-testid="oauth-apple-button"
          disabled={!appleReady || busy || disabledForConsent}
          onClick={handleApple}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 10, width: "100%", minHeight: 44,
            padding: "0.7rem 1rem", borderRadius: 999,
            background: "#000", color: "#fff", border: "none",
            fontSize: 15, fontWeight: 500,
            cursor: (appleReady && !busy && !disabledForConsent) ? "pointer" : "not-allowed",
            opacity: (appleReady && !disabledForConsent) ? 1 : 0.55,
            fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
          </svg>
          {busy ? "Signing in…"
                : (mode === "signup" ? "Sign up with Apple" : "Sign in with Apple")}
        </button>
      ) : null}
      <div className="oauth-buttons__divider" data-testid="oauth-divider"
           style={{
             display: "flex", alignItems: "center", gap: 10,
             color: "#8a827a", fontSize: 12, marginTop: 4,
             textTransform: "uppercase", letterSpacing: "0.08em",
           }}>
        <span style={{ flex: 1, height: 1, background: "#e5ded4" }} />
        <span>or continue with email</span>
        <span style={{ flex: 1, height: 1, background: "#e5ded4" }} />
      </div>
    </div>
  );
}
